/**
 * The app's one QueryClient factory (ADR-0056 + PROV-AUDIT).
 *
 * Extracted from `main.tsx` for one structural rule: **every successful
 * mutation invalidates the activity views.** Every admin mutation writes
 * an audit row, so after any write the "who did what, when" timelines
 * are stale by definition — an operator who just revoked cohort access
 * must see that event in the page's Activity section, not the cache's
 * memory of before their click. Naming `queryKeys.activity` in every
 * `useMutation` would be ~15 hand-maintained copies of the same fact;
 * a forgotten one is a silently stale timeline, which is exactly the
 * class of bug the staleness helpers exist to prevent. The mutation
 * cache states it once, and future mutations inherit it for free.
 *
 * Cheap by construction: invalidation only refetches MOUNTED activity
 * queries — at most the one detail-page section (10 rows) or the
 * Activity page. Everything else a mutation makes stale keeps being
 * named per mutation via the `keysStaleAfter…` helpers; those are
 * data-shaped judgments, not a blanket rule like this one.
 */
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { queryKeys } from './query-keys.js'

export function createAdminQueryClient(): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      // Success only: a refused write committed nothing, so there is
      // no new history to show.
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.activity })
      },
    }),
    /**
     * The defaults are tuned for an operator tool rather than a consumer
     * app:
     *
     * - `retry: false` — an operator watching a table wants to see the
     *   failure now, not three silent attempts later. The transport
     *   already distinguishes NETWORK from a 5xx, so the page can say
     *   which it was.
     * - `refetchOnWindowFocus: false` — alt-tabbing to check an email
     *   should not reshuffle a roster somebody is mid-way through
     *   selecting rows in.
     * - a short `staleTime` — long enough that navigating client →
     *   cohort → back does not refetch the client three times, short
     *   enough that a second operator's change shows up on the next
     *   navigation.
     */
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  })
  return queryClient
}
